import React, {
    FC,
    MutableRefObject,
    ReactElement,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'
import { useSearchParams } from 'react-router-dom'
import useIsMountedRef from 'use-is-mounted-ref'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { PaginationInfo } from '../../../../NonReact/Types/Types'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import { AdminFilters, AdminFilterValues } from '../../Molecules/AdminFilters/AdminFilters'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { DataTable, DataTableColumn } from '../../Molecules/DataTable/DataTable'
import { displayError } from '../../../../React/Common/Utils/Messages'
import {
    DeviceListLanguage,
    DeviceModal,
    GenericAdminFilterLanguage,
    GenericAdminListLanguage,
    Router as RouterLang,
    SidebarMenu as SidebarMenuLanguage
} from '../../../../../i18n'
import { DEFAULT_ROWS_PER_PAGE, UseAdminStatState, useAdminTableState } from '../../../Hooks/useAdminStatState'
import { useAdminListLegacyServletSwitch } from '../../../Hooks/useAdminListLegacyServletSwitch'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'
import { IsStringEmpty, IsVarEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { DEVICE_ADMIN_LIST_FIELDS } from '../../../../NonReact/constants/ApiConstants'
import { ALL_VALUE } from '../../../../React/Common/Constants/DefaultValues'
import { getDeviceList } from '../../../../React/Common/API/ItemsGetters'
import { DeviceListItem, DeviceListResponse, DeviceListRow } from '../../../../NonReact/Types/DeviceListTypes'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import { ServletCmds } from '../../../../NonReact/constants/ServletCommands'
import { AdminListUrlBootstrap, parseAdminListUrlSearchParams } from '../../../Utils/adminListUrlParams'
import { buildAdminListRowKey } from '../../../Utils/adminListRowKeys'
import { hasResolvedInitialTelSysPartitionListFilters } from '../../../Utils/adminListFilterReadiness'
import { DeviceConfigurationModal } from '../../../../React/DeviceConfiguration/Modal/DeviceConfigurationModal'
import { TelSysTypes } from '../../../../NonReact/constants/TelephonyServerTypes'
import { DeviceListUsersCell, serialiseDeviceListUsers } from './index'

const DEFAULT_DEVICE_SORT: string = 'DeviceNumber:ASC'

/**
 * Returns {@code noneLabel} when {@code value} is empty or whitespace-only; otherwise returns {@code value}.
 *
 * @param value - Raw cell text from the device list row.
 * @param noneLabel - Localised label for missing values (for example {@code None}).
 * @returns Text to render in the table cell.
 */
const formatDeviceListOptionalCell = (value: string, noneLabel: string): string =>
    IsStringEmpty(value.trim()) ? noneLabel : value

/**
 * Routed Devices list view (partition-scoped).
 *
 * @returns Devices list page content.
 */
export const DeviceList: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(RouterLang.NAME)
    language.loadBundle(SidebarMenuLanguage.NAME)
    language.loadBundle(GenericAdminListLanguage.NAME)
    language.loadBundle(GenericAdminFilterLanguage.NAME)
    language.loadBundle(DeviceListLanguage.NAME)
    language.loadBundle(DeviceModal.NAME)

    const [deviceRows, setDeviceRows]: ReactState<DeviceListRow[]> = useState<DeviceListRow[]>([])
    const {
        loading,
        setLoading,
        sort,
        setSort,
        filters,
        setFilters,
        pagination,
        setPagination,
        totalCount,
        setTotalCount
    }: UseAdminStatState = useAdminTableState()
    const [userInfo]: UserInfo = useUserInfo()
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [searchParams] = useSearchParams()
    const urlBootstrap: Nullable<AdminListUrlBootstrap> = useMemo((): Nullable<AdminListUrlBootstrap> => {
        const parsed: AdminListUrlBootstrap = parseAdminListUrlSearchParams(searchParams)

        return Object.keys(parsed).length > 0 ? parsed : null
    }, [searchParams])
    const [hasResolvedInitialUrlBootstrapLoad, setHasResolvedInitialUrlBootstrapLoad]: ReactState<boolean> =
        useState<boolean>(() => IsVarEmpty(urlBootstrap))
    const [hasResolvedInitialFiltersLoad, setHasResolvedInitialFiltersLoad]: ReactState<boolean> =
        useState<boolean>(false)
    const [hasReceivedInitialFilters, setHasReceivedInitialFilters]: ReactState<boolean> = useState<boolean>(false)
    const isBootstrappingFromUrl: boolean = isVarNotEmpty(urlBootstrap) && IsVarEmpty(filters)
    const isWaitingForInitialUrlBootstrapLoad: boolean =
        isVarNotEmpty(urlBootstrap) && !hasResolvedInitialUrlBootstrapLoad
    const isWaitingForInitialFiltersLoad: boolean = !hasResolvedInitialFiltersLoad

    const [isDeviceModalOpen, setIsDeviceModalOpen]: ReactState<boolean> = useState<boolean>(false)
    const [isViewOnlyModal, setIsViewOnlyModal]: ReactState<boolean> = useState<boolean>(false)
    const [selectedDeviceRow, setSelectedDeviceRow]: ReactState<Nullable<DeviceListRow>> =
        useState<Nullable<DeviceListRow>>(null)

    const telSysRaw: Definable<string | number> = filters?.telephonyServer?.value
    const partRaw: Definable<string | number> = filters?.partition?.value
    const listTelSysId: Nullable<number> =
        isVarNotEmpty(telSysRaw) && telSysRaw !== ALL_VALUE ? Number(telSysRaw) : null
    const listPartitionId: Nullable<number> = isVarNotEmpty(partRaw) ? Number(partRaw) : null
    const listSearch: string = filters?.search ?? EMPTY_STRING
    const selectedTelSysType: Definable<TelSysTypes> = filters?.telephonyServer?.telSysType
    const showDeviceNumberInternalColumn: boolean =
        isVarNotEmpty(selectedTelSysType) && selectedTelSysType !== TelSysTypes.MS_TEAMS
    const showIdOnTelSysColumn: boolean = selectedTelSysType === TelSysTypes.MS_TEAMS
    const showQueueStatusColumn: boolean = selectedTelSysType === TelSysTypes.MS_TEAMS

    useAdminListLegacyServletSwitch(ServletCmds.ADMIN_DEVICES, filters, userInfo?.Scope?.CMEnvironmentID)

    /**
     * Loads device rows for the selected telephony server and partition.
     */
    const loadDeviceList = useCallback((): void => {
        if (!hasReceivedInitialFilters) return

        if (isBootstrappingFromUrl) return

        if (IsVarEmpty(listTelSysId) || IsVarEmpty(listPartitionId)) {
            setDeviceRows([])
            setTotalCount(0)
            setHasResolvedInitialUrlBootstrapLoad(true)
            setHasResolvedInitialFiltersLoad(true)
            return
        }

        setLoading((n: number): number => n + 1)

        const [currentPage, rowsPerPage]: PaginationInfo = pagination
        const searchQuery: Definable<string> = listSearch === EMPTY_STRING ? undefined : listSearch
        const sortExpr: string = sort ?? DEFAULT_DEVICE_SORT

        getDeviceList(
            listTelSysId,
            listPartitionId,
            currentPage * rowsPerPage,
            rowsPerPage,
            sortExpr,
            searchQuery,
            undefined,
            undefined,
            DEVICE_ADMIN_LIST_FIELDS
        )
            .then((response: Nullable<DeviceListResponse>): void => {
                if (!isMounted.current) return

                const items: DeviceListItem[] = response?.Data?.Items ?? []
                const pageStartIndex: number = currentPage * rowsPerPage
                const rows: DeviceListRow[] = items.map(
                    (item: DeviceListItem, index: number): DeviceListRow => ({
                        key: buildAdminListRowKey(
                            [
                                listTelSysId,
                                listPartitionId,
                                item.PartIDInTelSys,
                                item.DeviceNumber,
                                item.DeviceNumberInternal,
                                item.DeviceTypeID,
                                item.IDOnTelSys
                            ],
                            pageStartIndex,
                            index
                        ),
                        TelSysID: listTelSysId,
                        PartitionID: listPartitionId,
                        DeviceNumber: item.DeviceNumber ?? EMPTY_STRING,
                        DeviceNumberInternal: item.DeviceNumberInternal ?? EMPTY_STRING,
                        Description: item.Description ?? EMPTY_STRING,
                        PartitionDescription: item.PartitionDescription ?? EMPTY_STRING,
                        PartIDInTelSys:
                            isVarNotEmpty(item.PartIDInTelSys)
                                ? String(item.PartIDInTelSys)
                                : EMPTY_STRING,
                        DeviceTypeID: item.DeviceTypeID ?? 0,
                        ListDeviceTypeDisplay: item.DeviceType ?? item.ListDeviceTypeDisplay ?? EMPTY_STRING,
                        IDOnTelSys: item.IDOnTelSys ?? EMPTY_STRING,
                        QueueStatus: item.QueueStatus ?? EMPTY_STRING,
                        Users: serialiseDeviceListUsers(item.Users)
                    })
                )

                setDeviceRows(rows)
                setTotalCount(response?.Data?.Total ?? 0)
            })
            .catch(displayError)
            .finally((): void => {
                setHasResolvedInitialUrlBootstrapLoad(true)
                setHasResolvedInitialFiltersLoad(true)

                if (isMounted.current) setLoading((n: number): number => n - 1)
            })
    }, [
        hasReceivedInitialFilters,
        isBootstrappingFromUrl,
        listTelSysId,
        listPartitionId,
        listSearch,
        pagination,
        sort,
        setLoading,
        setTotalCount
    ])

    useEffect((): void => {
        setHasResolvedInitialUrlBootstrapLoad(IsVarEmpty(urlBootstrap))
    }, [urlBootstrap])

    const columns: DataTableColumn<DeviceListRow, keyof DeviceListRow>[] = useMemo(() => {
        const noneLabel: string = language.get(DeviceListLanguage.DeviceList_Value_None)
        const visibleColumns: DataTableColumn<DeviceListRow, keyof DeviceListRow>[] = [
            {
                headerName: language.get(DeviceListLanguage.DeviceList_Column_DeviceNumber),
                field: 'DeviceNumber',
                key: 'device-list-device-number',
                sortable: true
            },
            {
                headerName: language.get(DeviceListLanguage.DeviceList_Column_Description),
                field: 'Description',
                key: 'device-list-description',
                sortable: true,
                align: 'left'
            },
            {
                headerName: language.get(DeviceListLanguage.DeviceList_Column_Partition),
                field: 'PartIDInTelSys',
                key: 'device-list-partition',
                sortable: false,
                hideBelow: 'md',
                renderCell: (row: DeviceListRow): ReactNode =>
                    formatDeviceListOptionalCell(row.PartIDInTelSys, noneLabel)
            },
            {
                headerName: language.get(DeviceListLanguage.DeviceList_Column_DeviceType),
                field: 'ListDeviceTypeDisplay',
                key: 'device-list-device-type',
                sortable: true,
                serverSortField: 'DeviceTypeID'
            }
        ]

        if (showDeviceNumberInternalColumn) {
            visibleColumns.push({
                headerName: language.get(DeviceListLanguage.DeviceList_Column_DeviceNumberInternal),
                field: 'DeviceNumberInternal',
                key: 'device-list-device-number-internal',
                sortable: true,
                serverSortField: 'DeviceNumberInternal',
                renderCell: (row: DeviceListRow): ReactNode =>
                    formatDeviceListOptionalCell(row.DeviceNumberInternal, noneLabel)
            })
        }

        if (showIdOnTelSysColumn) {
            visibleColumns.push({
                headerName: language.get(DeviceListLanguage.DeviceList_Column_IDOnTelSys),
                field: 'IDOnTelSys',
                key: 'device-list-id-on-telsys',
                sortable: true,
                renderCell: (row: DeviceListRow): ReactNode => formatDeviceListOptionalCell(row.IDOnTelSys, noneLabel)
            })
        }

        if (showQueueStatusColumn) {
            visibleColumns.push({
                headerName: language.get(DeviceListLanguage.DeviceList_Column_QueueStatus),
                field: 'QueueStatus',
                key: 'device-list-queue-status',
                sortable: false
            })
        }

        visibleColumns.push({
            headerName: language.get(DeviceListLanguage.DeviceList_Column_Users),
            field: 'Users',
            key: 'device-list-users',
            sortable: false,
            hideBelow: 'md',
            disableRowSelection: true,
            renderCell: (row: DeviceListRow): ReactNode => (
                <DeviceListUsersCell
                    users={row.Users}
                    noneLabel={noneLabel}
                    moreUsersLabel={language.getInterpolated(
                        DeviceListLanguage.DeviceList_Users_MoreUsers,
                        row.Users.split('\n').filter((user: string): boolean => !IsStringEmpty(user.trim())).length - 2
                    )}
                />
            )
        })

        return visibleColumns
    }, [language, showDeviceNumberInternalColumn, showIdOnTelSysColumn, showQueueStatusColumn])

    useEffect((): void => {
        loadDeviceList()
    }, [loadDeviceList])

    const canOpenDeviceModal: boolean = isVarNotEmpty(listTelSysId) && isVarNotEmpty(listPartitionId)

    const openAddModal = useCallback((): void => {
        setSelectedDeviceRow(null)
        setIsViewOnlyModal(false)
        setIsDeviceModalOpen(true)
    }, [])

    const openChangeModal = useCallback((selectedRows: DeviceListRow[]): void => {
        setSelectedDeviceRow(selectedRows[0] ?? null)
        setIsViewOnlyModal(false)
        setIsDeviceModalOpen(true)
    }, [])

    const openViewModal = useCallback((selectedRows: DeviceListRow[]): void => {
        setSelectedDeviceRow(selectedRows[0] ?? null)
        setIsViewOnlyModal(true)
        setIsDeviceModalOpen(true)
    }, [])

    const closeDeviceModal = useCallback((): void => {
        setIsDeviceModalOpen(false)
        setSelectedDeviceRow(null)
        setIsViewOnlyModal(false)
    }, [])

    /**
     * Partition scope for {@link DeviceConfigurationModal}: when a list row is selected (change/view), use that row's
     * {@link DeviceListRow.PartIDInTelSys} and {@link DeviceListRow.PartitionDescription}; otherwise use filter scope.
     */
    const deviceModalPartitionId: number = useMemo((): number => {
        if (isVarNotEmpty(selectedDeviceRow)) {
            const raw: string = selectedDeviceRow.PartIDInTelSys.trim()

            if (isStringNotEmpty(raw)) {
                const parsed: number = Number.parseInt(raw, 10)

                if (Number.isFinite(parsed)) {
                    return parsed
                }
            }
        }

        return listPartitionId as number
    }, [selectedDeviceRow, listPartitionId])

    const deviceModalPartitionDisplayLabel: string = useMemo((): string => {
        if (isVarNotEmpty(selectedDeviceRow) && isStringNotEmpty(selectedDeviceRow.PartitionDescription.trim())) {
            return selectedDeviceRow.PartitionDescription.trim()
        }

        return filters?.partition?.label ?? EMPTY_STRING
    }, [selectedDeviceRow, filters?.partition?.label])

    return (
        <div className='device-list admin-list-container'>
            <PageTitle title={RouterLang.PageTitle_device} />
            <div className='device-list-table-container admin-table-list-container'>
                <AdminFilters
                    labelTelSysPartitionAllSentinelAsNone
                    selectFilterPlaceholderTooltips={{
                        telephonyServer: true
                    }}
                    localeText={{
                        cmeLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_CME_LABEL),
                        telephonyServerLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_TELSYS_LABEL
                        ),
                        partitionLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_PARTITION_LABEL
                        ),
                        searchLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_SEARCH_LABEL),
                        includeChildCmeLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_INCLUDE_CHILD_CMES_LABEL
                        )
                    }}
                    urlBootstrap={urlBootstrap}
                    onChange={(values: AdminFilterValues): void => {
                        setFilters(values)

                        if (hasResolvedInitialTelSysPartitionListFilters(values)) {
                            setHasReceivedInitialFilters(true)
                        }
                    }}
                    optionsToDisplay={{
                        cme: true,
                        telephonyServer: true,
                        partition: true,
                        crmType: false,
                        scope: false,
                        codeType: false,
                        userType: false,
                        includeCmeChildren: true,
                        search: true,
                        includeRefresh: true
                    }}
                    onRefresh={loadDeviceList}
                />
                <DataTable<DeviceListRow, keyof DeviceListRow>
                    permissionName={AdminBackendPermissionNames.DEVICE}
                    localeText={{
                        addButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Add),
                        viewButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_View),
                        changeButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Change),
                        deleteButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Delete),
                        selectionActive: (count: number): string =>
                            language.getInterpolated(GenericAdminListLanguage.GenericAdminList_Footer_Selected, count),
                        noRows: language.get(GenericAdminListLanguage.GenericAdminList_Footer_NoRows)
                    }}
                    columns={columns}
                    data={deviceRows}
                    defaultRowsPerPageOptions={[10, 25, 50, 100]}
                    defaultRowsPerPage={DEFAULT_ROWS_PER_PAGE}
                    totalCount={totalCount}
                    isLoading={loading > 0 || isWaitingForInitialUrlBootstrapLoad || isWaitingForInitialFiltersLoad}
                    paginationServer
                    sortServer
                    isSelectable
                    colIndexToTriggerClick={1}
                    onSortChange={setSort}
                    toolbarAddDisabled={!canOpenDeviceModal}
                    toolbarAddDisabledTitle={language.get(
                        DeviceListLanguage.DeviceList_Add_Disabled_SelectTelephonyServer
                    )}
                    onToolbarAddClick={openAddModal}
                    onToolbarChangeClick={openChangeModal}
                    onToolbarViewClick={openViewModal}
                    onRowsPerPageChange={(currentPage: number, newRowsPerPage: number): void =>
                        setPagination([currentPage, newRowsPerPage])
                    }
                    onPageChange={(newPage: number, rowsPerPage: number): void => setPagination([newPage, rowsPerPage])}
                />
                {isDeviceModalOpen &&
                canOpenDeviceModal &&
                isVarNotEmpty(listTelSysId) &&
                isVarNotEmpty(listPartitionId) ? (
                    <DeviceConfigurationModal
                        key={`device-modal-${selectedDeviceRow?.DeviceNumber ?? 'add'}-${
                            isViewOnlyModal ? 'view' : 'edit'
                        }`}
                        refreshAction={loadDeviceList}
                        filterTelSysID={listTelSysId}
                        filterPartitionID={deviceModalPartitionId}
                        partitionDisplayLabel={deviceModalPartitionDisplayLabel}
                        telSysType={selectedTelSysType}
                        selectedDeviceNumber={
                            isVarNotEmpty(selectedDeviceRow?.DeviceNumber) &&
                            selectedDeviceRow.DeviceNumber !== EMPTY_STRING
                                ? selectedDeviceRow.DeviceNumber
                                : undefined
                        }
                        viewOnly={isViewOnlyModal}
                        onModalExited={closeDeviceModal}
                    />
                ) : (
                    <></>
                )}
            </div>
        </div>
    )
}
